import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { knowledgeWikiApi } from '../lib/api.js';
import { queryKeys } from '../lib/query-keys.js';
import type { KnowledgeWikiFilter, KnowledgeWikiUpdateInput } from '../types/knowledge-wiki.js';

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business knowledge-wiki のドキュメント一覧を TanStack Query 経由で取得。絞り込み条件とファセットを返す
 * @graph-connects /knowledge-wiki/documents [calls] ドキュメント一覧取得API
 * @graph-connects knowledgeWikiApi.list [calls] 一覧APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useKnowledgeWikiDocuments(filter: KnowledgeWikiFilter = {}) {
  return useQuery({
    queryKey: queryKeys.knowledgeWiki.list(filter),
    queryFn: () => knowledgeWikiApi.list(filter),
    // 絞り込みを変えるたびに前の結果を捨てると、その瞬間だけスケルトンに戻って
    // 一覧が消える（検索中は入力欄ごと描き替わってフォーカスも失う）。
    // 新しい結果が届くまで前の結果を出したままにする。
    placeholderData: keepPreviousData,
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business knowledge-wiki のドキュメント詳細を TanStack Query 経由で取得。項目別の構造化本文を返す
 * @graph-connects /knowledge-wiki/documents/:id [calls] ドキュメント詳細取得API
 * @graph-connects knowledgeWikiApi.getDetail [calls] 詳細APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useKnowledgeWikiDocument(id: string, locale?: string) {
  return useQuery({
    queryKey: queryKeys.knowledgeWiki.detail(id, locale),
    queryFn: () => knowledgeWikiApi.getDetail(id, locale),
    enabled: id.length > 0,
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business knowledge-wiki の項目別編集を保存する mutation。保存後は一覧・詳細のキャッシュを無効化する
 * @graph-connects /knowledge-wiki/documents/:id [calls] ドキュメント更新API
 * @graph-connects knowledgeWikiApi.update [calls] 更新APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useUpdateKnowledgeWikiDocument(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: KnowledgeWikiUpdateInput) => knowledgeWikiApi.update(id, input),
    onSuccess: async () => {
      // 未記入バッジと件数が一覧側にも出るため、詳細だけでなく一覧も無効化する
      await queryClient.invalidateQueries({ queryKey: queryKeys.knowledgeWiki.all });
    },
  });
}
